import React, { useState, useEffect } from 'react';
import { 
  Building, Building2, Settings, Key, Users, HelpCircle, Save, Upload, X,
  Mail, Phone, MapPin, Calendar, Shield, AlertCircle, CheckCircle,
  Clock, Globe, DollarSign, User, Lock, Edit2, Image as ImageIcon,
  FileText, Smartphone, MessageCircle, ExternalLink, Copy, Check,
  Fingerprint, ArrowRight, ChevronRight, Sparkles,
  Calculator
} from 'lucide-react';
import api from '../services/api'; 
import { Toast } from '../components/ui/Toast';
import ParametresPointage from './ParametresPointage';
import CompanySettings from './CompanySettings';
import DepartmentsManagement from './DepartmentsManagement';
import ModeleContrat from './ModeleContrat';

import LicenceSection from './LicenceSection';
import GestionUtilisateurs from './GestionUtilisateurs';
import ParametresComptabilite from './comptabilite/ParametresComptabilite';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import GuideParametresPointage from './GuideParametresPointage';
import GuideModeleContrat from './GuideModeleContrat';
import GuideGestionUtilisateurs from './GuideGestionUtilisateurs';

/**
 * PARAMÈTRES RH - VERSION MODERNE AVEC CARTES
 * Design dashboard avec cartes cliquables
 */
const ParametresRH = () => {
  const [showGuidePointage, setShowGuidePointage] = useState(false);
  const [showGuideContrat, setShowGuideContrat] = useState(false);
  const [showGuideUtilisateurs, setShowGuideUtilisateurs] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  
  const [licence, setLicence] = useState({
    numero_licence: '', version_app: '', date_debut: '', date_fin: '',
    statut: '', nb_utilisateurs_max: 0, nb_utilisateurs_actifs: 0, type_licence: ''
  });
  
  // 🔥 Feature Flag Compta
  const { isEnabled: comptaEnabled } = useFeatureFlag('compta_module');
  const { isEnabled: rfidModeEnabled } = useFeatureFlag('rfid_attendance');
  const { isEnabled: photoEnabled } = useFeatureFlag('pointage_photo');
  const { isEnabled: contratsEnabled } = useFeatureFlag('contrats_employes'); 


  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => {
        setToast({ show: false, message: '', type: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  useEffect(() => {
    loadAllSettings();
  }, []);

  const loadAllSettings = async () => {
    setLoading(true);
    try {
      console.log('✅ [Paramètres] Chargement temporairement désactivé');
    } catch (error) {
      console.error('Erreur chargement paramètres:', error);
      showToast('Erreur lors du chargement des paramètres', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Configuration des cartes avec uniquement vos couleurs
  const dashboardCards = [
    {
      id: 'entreprise',
      title: 'Entreprise',
      description: 'Informations générales et coordonnées',
      icon: Building,
      color: '#fed774',
      stats: { label: 'Configuration', value: 'Complète' }
    },
    {
      id: 'departments',
      title: 'Départements',
      description: 'Services et équipes',
      icon: Building2,
      color: '#fed774',
      stats: { label: 'Départements', value: '—' }
    },
  
    {
      id: 'contrat',
      title: 'Contrat',
      description: 'Modèles de contrats',
      icon: FileText,
      color: '#fed774',
      stats: { label: 'Modèles', value: '—' }
    },
    {
      id: 'pointage',
      title: 'Pointage',
      description: 'Système et horaires',
      icon: Fingerprint,
      color: '#fed774',
      stats: { label: 'Système', value: 'Actif' }
    },

 

    {
      id: 'licence',
      title: 'Licence',
      description: 'Abonnement et licences',
      icon: Shield,
      color: '#fdac84',
      stats: { label: 'Statut', value: 'Active' }
    },
    {
      id: 'comptes',
      title: 'Comptes utilisateurs',
      description: 'Gestion des accès',
      icon: Users,
      color: '#fed774',
      stats: { label: 'Utilisateurs', value: '—' }
    },
    {
      id: 'comptabilite',
      title: 'Comptabilité',
      description: 'Devise, numérotation et préférences',
      icon: Calculator,
      color: '#448696',
      stats: { label: 'Configuration', value: '—' }
    },
  ];


  // 🔥 Filtrer les cartes selon les feature flags
  const filteredCards = dashboardCards.filter(card => {
    if (card.id === 'comptabilite') return comptaEnabled;
     if (card.id === 'contrat') return contratsEnabled;
    return true;
  });

  // Si une section est active
  if (activeSection) {
    return (
      <div className="min-h-screen p-6" style={{ backgroundColor: '#fdfcf5' }}>
        <div className="mb-8">
          <button
            onClick={() => {
              setActiveSection(null);
              setShowGuidePointage(false);
              setShowGuideContrat(false);
              setShowGuideUtilisateurs(false);
            }}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 font-semibold transition-colors group"
          >
            <div className="w-8 h-8 rounded-lg flex items-center justify-center group-hover:bg-gray-100 transition-colors">
              <ChevronRight size={20} className="rotate-180" />
            </div>
            Retour aux paramètres
          </button>
          
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div 
                className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg"
                style={{ 
                  backgroundColor: filteredCards.find(c => c.id === activeSection)?.color 
                }}
              >
                {React.createElement(filteredCards.find(c => c.id === activeSection)?.icon, {
                  size: 28,
                  style: { color: '#1f2937' }
                })}
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {filteredCards.find(c => c.id === activeSection)?.title}
                </h1>
                <p className="text-gray-600 text-sm font-medium">
                  {filteredCards.find(c => c.id === activeSection)?.description}
                </p>
              </div>
            </div>

            {/* Bouton guide — conditionnel selon la section */}
            {activeSection === 'pointage' && (
              <button
                onClick={() => setShowGuidePointage(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 hover:text-[#c97a4e] hover:bg-[#c97a4e]/10 transition-all border border-gray-200 hover:border-[#c97a4e]/30"
              
              >
                <HelpCircle size={14} />
                Guide de configuration
              </button>
            )}
            {activeSection === 'contrat' && (
              <button
                onClick={() => setShowGuideContrat(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 hover:text-[#92700f] hover:bg-[#92700f]/10 transition-all border border-gray-200 hover:border-[92700f]/30"
               
              >
                <HelpCircle size={14} />
                Guide du modèle de contrat
              </button>
            )}

            {activeSection === 'comptes' && (
              <button
                onClick={() => setShowGuideUtilisateurs(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 hover:text-[#336b78] hover:bg-[#336b78]/10 transition-all border border-gray-200 hover:border-[92700f]/30"
               
              >
                <HelpCircle size={14} />
                Guide des comptes utilisateurs
              </button>
            )}


          </div>


        </div>

        {activeSection === 'entreprise' && <CompanySettings />}
        {activeSection === 'departments' && <DepartmentsManagement />}
        {activeSection === 'contrat' && <ModeleContrat />}
        {activeSection === 'pointage' && <ParametresPointage />}
        
      
        {activeSection === 'licence' && <LicenceSection />}
        {activeSection === 'comptes' && <GestionUtilisateurs />}
        {activeSection === 'comptabilite' && <ParametresComptabilite />}


       {showGuidePointage && (
          <GuideParametresPointage
            onClose={() => setShowGuidePointage(false)}
            rfidEnabled={rfidModeEnabled}
            photoEnabled={photoEnabled}
          />
        )}

        {showGuideContrat && (
          <GuideModeleContrat
            onClose={() => setShowGuideContrat(false)}
          />
        )}

         {showGuideUtilisateurs && (
          <GuideGestionUtilisateurs
            onClose={() => setShowGuideUtilisateurs(false)}
          />
        )}
       

        {toast.show && (
          <Toast
            show={toast.show}
            message={toast.message}
            type={toast.type}
            onClose={() => setToast({ show: false, message: '', type: '' })}
          />
        )}



      </div>
    );
  }

  // Dashboard principal
  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#fdfcf5' }}>
        <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2" style={{ color: "#448696" }}>Paramètres</h1>
        <p className="text-gray-600 text-base font-medium">Configurez les paramètres de votre entreprise</p>
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
          <div 
            className="animate-spin w-12 h-12 border-4 rounded-full mx-auto mb-4" 
            style={{ borderColor: '#448696', borderTopColor: 'transparent' }}
          />
          <p className="text-sm font-medium text-gray-600">Chargement...</p>
        </div>
      ) : (

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">


       {filteredCards.map((card) => (
            <DashboardCard 
              key={card.id}
              card={card}

              onClick={() => {
                setShowGuidePointage(false);
                setShowGuideContrat(false);
                setShowGuideUtilisateurs(false);
                setActiveSection(card.id);
              }}
            />
          ))}


        </div>
      )}

      {toast.show && (
        <Toast
          show={toast.show}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: '' })}
        />
      )}
    </div>
  );
};

// Composant Carte Dashboard - Version compacte
const DashboardCard = ({ card, onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const Icon = card.icon;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 cursor-pointer transition-all duration-300 relative overflow-hidden group"
      style={{
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: isHovered ? '0 12px 24px rgba(0, 0, 0, 0.08)' : '0 1px 3px rgba(0, 0, 0, 0.05)'
      }}
    >
      {/* Icon avec couleur */}
      <div 
        className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 shadow-sm transition-transform duration-300"
        style={{ 
          backgroundColor: card.color,
          transform: isHovered ? 'scale(1.05)' : 'scale(1)'
        }}
      >
        <Icon 
        size={24} 
        strokeWidth={2.5}
        style={{ color: '#1f2937' }}
      />
      </div>

      {/* Titre et description */}
      <h3 className="text-base font-bold text-gray-900 mb-1">
        {card.title}
      </h3>
      <p className="text-gray-600 text-xs font-medium mb-3 leading-relaxed">
        {card.description}
      </p>

      {/* Stats */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
     
        
        {/* Flèche */}
        <div 
          className="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-300"
          style={{
            backgroundColor: isHovered ? card.color + '15' : 'transparent',
            transform: isHovered ? 'translateX(2px)' : 'translateX(0)'
          }}
        >
          <ArrowRight 
            size={16} 
            style={{ color: card.color }}
            strokeWidth={2.5}
          />
        </div>
      </div>

      {/* Effet de fond au hover */}
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity duration-300 pointer-events-none"
        style={{ backgroundColor: card.color }}
      />
    </div>
  );
};


export default ParametresRH;